import React, { useEffect } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import BackButton from '../../../custom/BackButton';
import ActionIcon from '../../../custom/ActionIcon';
import { IUser, TransferTruck } from '../../../../globals/interfaces';
import useTypedForm from '../../../custom/hookForm/useTypedForm';
import { yupResolver } from '@hookform/resolvers/yup';
import { string, object, date } from 'yup';
import { getDate, getGuid, getTsDate } from '../../../../utils';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    user: IUser;
    handleCreateUpdateTruck: (data: TransferTruck) => void;
    deleteTruck: (id: number) => Promise<void>;
    selectedTruck?: TransferTruck;
}

export default function ModalManageTruck({
    modal,
    setModal,
    user,
    handleCreateUpdateTruck,
    deleteTruck,
    selectedTruck
}: Props) {

    const update = Boolean(selectedTruck);
    const {
        formState: {
            errors,
            isValid,
            isDirty
        },
        trigger,
        reset,
        handleSubmit,
        FormInput
    } = useTypedForm<TransferTruck>({
        resolver: yupResolver(object().shape({
            name: string().required(),
            modifiedBy: string().required(),
            modifiedAt: date().required(),
            guid: string().required(),
            unit: string().required(),
            status: string().required()
        }))
    });

    useEffect(() => {
        const metaData: Partial<TransferTruck> = {
            modifiedBy: user.s_email,
            modifiedAt: getTsDate(),
        }
        if (selectedTruck) {
            reset({
                ...selectedTruck,
                ...metaData
            });
        } else {
            reset({
                ...metaData,
                guid: getGuid(),
                unit: user.s_unit,
                status: 'AVAILABLE'
            });
        }
    }, [modal, selectedTruck]);

    const toggle = () => setModal(!modal);

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>{update ? 'Update' : 'Create'} Truck</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <FormInput
                    label='Name'
                    name={'name'}
                    normalCase={false}
                />
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <FooterButtonsContainer update={update}>
                        {
                            update && (
                                <ActionIcon
                                    type={'delete'}
                                    onClick={() => deleteTruck(selectedTruck.id)}
                                />
                            )
                        }
                        <ActionIcon
                            type={'save'}
                            onClick={handleSubmit(handleCreateUpdateTruck)}
                            disabled={!isValid || !isDirty}
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;